import React, { createContext, useContext, useState, useEffect } from 'react';
import api from './api';

interface Kullanici {
  id: string;
  ad: string;
  email: string;
  rol: 'super_admin' | 'sube_admin' | 'egitmen' | 'ogrenci' | 'admin';
  foto_url?: string;
  sube_id?: string;
  sube_adi?: string;
  izinler?: Record<string, boolean>;
  is_super_admin?: boolean;
}

interface AuthContextType {
  kullanici: Kullanici | null;
  token: string | null;
  yukleniyor: boolean;
  impersonating: string | null;
  subeGirisi: string | null;
  cikisYap: () => void;
  impersonate: (token: string, ad: string, soyad: string) => void;
  geriDon: () => void;
  subeGir: (subeId: string, subeAdi: string) => Promise<void>;
  subedenCik: () => void;
}

const AuthContext = createContext<AuthContextType>({
  kullanici: null,
  token: null,
  yukleniyor: true,
  impersonating: null,
  subeGirisi: null,
  cikisYap: () => {},
  impersonate: () => {},
  geriDon: () => {},
  subeGir: async () => {},
  subedenCik: () => {},
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [kullanici, setKullanici] = useState<Kullanici | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [yukleniyor, setYukleniyor] = useState(true);
  const [impersonating, setImpersonating] = useState<string | null>(
    localStorage.getItem('impersonating_as')
  );
  const [subeGirisi, setSubeGirisi] = useState<string | null>(
    localStorage.getItem('sube_girisi')
  );

  useEffect(() => {
    if (token) {
      setYukleniyor(true);
      api.get('/api/auth/beni-getir')
        .then(res => setKullanici(res.data))
        .catch((err) => {
          if (err.response?.status === 401 || err.response?.status === 403) {
            localStorage.removeItem('token');
            setToken(null);
          }
        })
        .finally(() => setYukleniyor(false));
    } else {
      setYukleniyor(false);
    }
  }, [token]);

  const cikisYap = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('admin_token_backup');
    localStorage.removeItem('impersonating_as');
    localStorage.removeItem('sube_giren_token_backup');
    localStorage.removeItem('sube_girisi');
    setToken(null);
    setKullanici(null);
    setImpersonating(null);
    setSubeGirisi(null);
    window.location.href = '/login';
  };

  const impersonate = (newToken: string, ad: string, soyad: string) => {
    const current = localStorage.getItem('token') ?? '';
    localStorage.setItem('admin_token_backup', current);
    localStorage.setItem('token', newToken);
    const isim = `${ad} ${soyad}`;
    localStorage.setItem('impersonating_as', isim);
    setImpersonating(isim);
    setKullanici(null);
    setToken(newToken);
  };

  const geriDon = () => {
    const backup = localStorage.getItem('admin_token_backup');
    if (!backup) return;
    localStorage.setItem('token', backup);
    localStorage.removeItem('admin_token_backup');
    localStorage.removeItem('impersonating_as');
    setImpersonating(null);
    setKullanici(null);
    setToken(backup);
    window.location.href = '/';
  };

  const subeGir = async (subeId: string, subeAdi: string) => {
    const res = await api.post(`/api/auth/sube-gir/${subeId}`);
    const current = localStorage.getItem('token') ?? '';
    localStorage.setItem('sube_giren_token_backup', current);
    localStorage.setItem('token', res.data.token);
    localStorage.setItem('sube_girisi', subeAdi);
    setSubeGirisi(subeAdi);
    setKullanici(null);
    setToken(res.data.token);
    window.location.href = '/';
  };

  const subedenCik = () => {
    const backup = localStorage.getItem('sube_giren_token_backup');
    if (!backup) return;
    localStorage.setItem('token', backup);
    localStorage.removeItem('sube_giren_token_backup');
    localStorage.removeItem('sube_girisi');
    setSubeGirisi(null);
    setKullanici(null);
    setToken(backup);
    window.location.href = '/';
  };

  return (
    <AuthContext.Provider value={{
      kullanici, token, yukleniyor, impersonating, subeGirisi,
      cikisYap, impersonate, geriDon, subeGir, subedenCik,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

// Granüler izin kontrolü — super_admin ve sube'ye girmiş super_admin her zaman true döner
export const useIzin = (izinAdi: string): boolean => {
  const { kullanici, subeGirisi } = useContext(AuthContext);
  if (!kullanici) return false;
  if (kullanici.rol === 'super_admin') return true;
  if (kullanici.is_super_admin || subeGirisi !== null) return true;
  return !!(kullanici.izinler?.[izinAdi]);
};

export default AuthContext;
